
import React, {
	createContext,
	useState,
	useEffect,
	useContext,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { toast } from "@/components/ui/use-toast";

interface SupabaseProfile {
	id: string;
	email: string;
	first_name: string;
	last_name: string;
	role: string;
}

// Add this to your existing types or create a new one
interface Organization {
	id: string;
	name: string;
	email_sending_enabled?: boolean;
	// Add other fields as needed
}

interface AuthContextValue {
	user: User | null;
	loading: boolean;
	userRole: string;
	isAdmin: boolean;
	isSuperAdmin: boolean;
	signIn: (email: string, password: string) => Promise<{ error?: Error }>;
	signOut: () => Promise<void>;
	signUp: (email: string, password: string) => Promise<void>;
	forgotPassword: (email: string) => Promise<{ error?: Error }>;
	organizations: Organization[];
	currentOrganization: Organization | null;
	refreshUserData: () => Promise<void>;
	userProfile: SupabaseProfile | null;
	subscriptionIssue: string | null;
	isInGracePeriod: boolean;
	limitIssue: { candidates: string | null; jobs: string | null } | null;
	isLimitInGracePeriod: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [userProfile, setUserProfile] = useState<SupabaseProfile | null>(null);
	const [userRole, setUserRole] = useState<string>("user");
	const [organizations, setOrganizations] = useState<Organization[]>([]);
	const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
	const [subscriptionIssue, setSubscriptionIssue] = useState<string | null>(null);
	const [subscriptionIssueSince, setSubscriptionIssueSince] = useState<string | null>(null);
	const [paymentGraceDays, setPaymentGraceDays] = useState<number>(10);
	const [skipSubscriptionCheck, setSkipSubscriptionCheck] = useState<boolean>(false);
	const [limitIssue, setLimitIssue] = useState<{ candidates: string | null; jobs: string | null } | null>(null);
	const [limitIssueSince, setLimitIssueSince] = useState<string | null>(null);
	const [authInitialized, setAuthInitialized] = useState(false);

	const isAdmin = userRole === "admin";
	const isSuperAdmin = userRole === "super_admin";

	const isInGracePeriod = !skipSubscriptionCheck
		&& !!subscriptionIssueSince
		&& (Date.now() - new Date(subscriptionIssueSince).getTime()) < paymentGraceDays * 24 * 60 * 60 * 1000;

	const isLimitInGracePeriod = !skipSubscriptionCheck
		&& !!limitIssueSince
		&& (Date.now() - new Date(limitIssueSince).getTime()) < paymentGraceDays * 24 * 60 * 60 * 1000;

	const signIn = async (email: string, password: string) => {
		setLoading(true);
		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email,
				password,
			});
			if (error) throw error;
			setUser(data.user);
			return { error: undefined };
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
			return { error };
		} finally {
			setLoading(false);
		}
	};

	const signOut = async () => {
		setLoading(true);
		try {
			const { error } = await supabase.auth.signOut();
			if (error) throw error;
			setUser(null);
			setUserProfile(null);
			setUserRole("user");
			setOrganizations([]);
			setCurrentOrganization(null);
			setSubscriptionIssue(null);
			setSubscriptionIssueSince(null);
			setPaymentGraceDays(10);
			setSkipSubscriptionCheck(false);
			setLimitIssue(null);
			setLimitIssueSince(null);
			toast({
				title: "Success",
				description: "Signed out successfully",
			});
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};
	const signUp = async (email: string, password: string) => {
		setLoading(true);
		try {
			const { data, error } = await supabase.auth.signUp({
				email,
				password,
			});
			if (error) throw error;
			setUser(data.user);
			toast({
				title: "Success",
				description: "Signed up successfully. Check your email to verify.",
			});
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	const forgotPassword = async (email: string) => {
		setLoading(true);
		try {
			const isLocalhost = () => {
				return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
			};

			const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
				redirectTo: isLocalhost() ? "http://localhost:8080/reset-password" : `https://${window.location.hostname}/reset-password`,
			});

			if (error) throw error;

			toast({
				title: "Success",
				description: "Password reset email sent. Check your inbox.",
			});

			return { error: undefined };
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
			return { error };
		} finally {
			setLoading(false);
		}
	};

	// Fetch organizations for the current user - extracted to prevent nesting in useEffect
	const fetchOrganizations = async (userId: string) => {
		try {
			// Get all organizations the user belongs to
			const { data: userOrgs, error: userOrgsError } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', userId);

			if (userOrgsError) throw userOrgsError;
			
			if (userOrgs && userOrgs.length > 0) {
				// Get details for each organization
				const orgIds = userOrgs.map(org => org.organization_uuid);
				const { data: organizations, error: orgsError } = await supabase
					.from('organizations')
					.select('*');
					//.in('id', orgIds);
				
				if (orgsError) throw orgsError;

				//console.log("Organizations:", organizations);
				
				if (organizations && organizations.length > 0) {

					setOrganizations(organizations);
					
					// Find the current organization (the one marked as current for this user)
					const { data: userOrg, error: currentOrgError } = await supabase
						.from('user_organization')
						.select('organization_uuid')
						.eq('user_uuid', userId)
						.single();
					
					if (!currentOrgError && userOrg) {
						// Find this organization in the organizations array
						const currentOrg = organizations.find(org => org.id === userOrg.organization_uuid);
						if (currentOrg) {
							setCurrentOrganization(currentOrg);
						} else if (organizations.length > 0) {
							// Fallback to first organization if no current org found
							setCurrentOrganization(organizations[0]);
						}

						// Fetch subscription issue and limit issue for the current org
						const currentOrgId = userOrg?.organization_uuid ?? (organizations[0]?.id ?? null);
						if (currentOrgId) {
							const { data: sub } = await supabase
								.from('organization_subscription')
								.select('subscription_issue, subscription_issue_since, payment_grace_days, skip_subscription_check, limit_issue, limit_issue_since')
								.eq('id', currentOrgId)
								.maybeSingle();

							setSubscriptionIssue(sub?.subscription_issue ?? null);
							setSubscriptionIssueSince(sub?.subscription_issue_since ?? null);
							setPaymentGraceDays(sub?.payment_grace_days ?? 10);
							setSkipSubscriptionCheck(sub?.skip_subscription_check ?? false);
							setLimitIssue(sub?.limit_issue ?? null);
							setLimitIssueSince(sub?.limit_issue_since ?? null);
						}
					}
				}
			}
		} catch (error) {
			console.error("Error fetching organizations:", error);
		}
	};

	// Get user profile data - extracted to prevent nesting in useEffect
	const fetchUserProfile = async (userId: string) => {
		try {
			const { data, error } = await supabase
				.from('profiles')
				.select('*')
				.eq('id', userId)
				.single();
			
			if (error) throw error;
			
			if (data) {
				setUserProfile(data);
				const role = data.role || 'user';
				setUserRole(role);
			}
		} catch (error) {
			console.error("Error fetching user profile:", error);
		} finally {
			setLoading(false);
		}
	};

	// Refresh user data including organizations
	const refreshUserData = async () => {
		if (!user?.id) return;
		
		try {
			// Update user profile data
			await fetchUserProfile(user.id);
			
			// Also refresh organizations
			await fetchOrganizations(user.id);
			
		} catch (error) {
			console.error("Error refreshing user data:", error);
		}
	};

	useEffect(() => {
		// Prevent multiple initialization cycles
		if (authInitialized) return;

		const initAuth = async () => {
			try {
				// First check if there's an existing session
				const { data: sessionData } = await supabase.auth.getSession();
				const initialSession = sessionData?.session;

				if (initialSession?.user) {
					setUser(initialSession.user);
					await fetchUserProfile(initialSession.user.id);
					await fetchOrganizations(initialSession.user.id);
				} else {
					setLoading(false);
				}
				
				// Track the current user ID to detect actual user changes
				let currentUserId = initialSession?.user?.id || null;
				
				// Set up the auth state change listener after initial check
				const { data: authListener } = supabase.auth.onAuthStateChange(
					async (event, session) => {
						const newUserId = session?.user?.id || null;
						
						// Only react to actual user changes (sign in/out), not storage sync events
						// TOKEN_REFRESHED events with same user don't need refetch
						const userChanged = newUserId !== currentUserId;
						
						// Skip all updates if user hasn't changed (prevents re-renders)
						if (!userChanged)
							return;
						
						currentUserId = newUserId;
						
						// Update user state
						setUser(session?.user || null);

						// Fetch data for new user or clear data on sign out
						if (session?.user) {
							setTimeout(() => {
								fetchUserProfile(session.user.id);
								fetchOrganizations(session.user.id);
							}, 0);
						} else {
							setUserProfile(null);
							setUserRole("user");
							setOrganizations([]);
							setCurrentOrganization(null);
							setSubscriptionIssue(null);
							setLimitIssue(null);
							setLimitIssueSince(null);
							setLoading(false);
						}
					}
				);
				
				setAuthInitialized(true);
				
				return () => {
					authListener.subscription.unsubscribe();
				};
			} catch (error) {
				console.error("Auth initialization error:", error);
				setLoading(false);
			}
		};

		initAuth();
	}, [authInitialized]);
		const authValue = {
		user,
		loading,
		userRole,
		isAdmin,
		isSuperAdmin,
		signIn,
		signOut,
		signUp,
		forgotPassword,
		organizations,
		currentOrganization,
		refreshUserData,
		userProfile,
		subscriptionIssue: skipSubscriptionCheck ? null : subscriptionIssue,
		isInGracePeriod,
		limitIssue,
		isLimitInGracePeriod,
	};
	
	return (
		<AuthContext.Provider value={authValue}>
			{children}
		</AuthContext.Provider>
	);
};
